import React from 'react';
import {logger} from '../../helperFunctions';

import {
  addPaymentMethod as addPaymentMethodAPI,
  removePaymentMethod as removePaymentMethodAPI,
  fetchPaymentToken as fetchPaymentTokenAPI,
  existingPaymentMethodPurchase as existingPaymentMethodPurchaseAPI,
  existingPaymentMethodAddBalance as existingPaymentMethodAddBalanceAPI,
  oneTimePaymentPurchase as oneTimePaymentPurchaseAPI,
  oneTimePaymentAddBalance as oneTimePaymentAddBalanceAPI,
  createAutoLoadProfile as createAutoLoadProfileAPI,
  disableAutoLoadTransitAccount as disableAutoLoadTransitAccountAPI,
  createAutoRenewProfile as createAutoRenewProfileAPI,
  disableAutoRenewProfile as disableAutoRenewProfileAPI
} from '../../Api';
import useOneTimePaymentToken from './useOneTimePaymentToken';

const usePayment = ({
  account,
  handleApiCallWithToken,
  handleApiRes,
  authAndContent,
  setLoading
}) => {
  const [paymentProcessing, setPaymentProcessing] = React.useState(false);
  const [selectedCardID, setSelectedCardID] = React.useState();
  const {
    oneTimePaymentToken,
    setOneTimePaymentToken,
    oneTimePaymentTimeLeft,
    addOneTimePaymentMethod,
    removeOneTimePaymentMethod
  } = useOneTimePaymentToken({authAndContent});

  React.useEffect(() => {
    if (account.account?.paymentMethods?.length > 0) {
      setSelectedCardID(account.account?.paymentMethods?.[0]?.id);
    }
  }, [account.account]);

  const addPaymentMethod = (...args) =>
    handleApiCallWithToken(addPaymentMethodAPI, ...args).then(res =>
      handleApiRes({
        res,
        successMsg: 'Card Added',
        errorMsg: 'Add Payment Failed'
      })
    );

  const removePaymentMethod = async id => {
    setLoading(true);
    return authAndContent.getValidToken().then(newToken =>
      removePaymentMethodAPI(newToken, id).then(ret => {
        if (!ret || ret.errors) {
          setLoading(false);
          logger('removePaymentMethodAPI failed: ', ret);
          return ret;
        }
        return new Promise(resolve =>
          setTimeout(() => {
            authAndContent.setSnackBarMessage('Card Removed');
            return account.fetchAccount(true);
          }, 3000)
        );
      })
    );
  };

  const fetchPaymentToken = async () => {
    setLoading(true);
    return authAndContent.getValidToken().then(newToken =>
      fetchPaymentTokenAPI(newToken, authAndContent.locale).then(
        paymentToken => {
          setLoading(false);
          return paymentToken;
        }
      )
    );
  };

  const paymentFlow = React.useCallback(
    api => params => {
      setLoading(true);
      setPaymentProcessing(true);
      return authAndContent
        .getValidToken()
        .then(newToken =>
          api(newToken, params).then(ret => {
            if (
              !ret?.errors &&
              (api === oneTimePaymentPurchaseAPI ||
                api === oneTimePaymentAddBalanceAPI)
            ) {
              removeOneTimePaymentMethod(); // if one time payment was successful, remove one time payment methode
            }
            setLoading(false);
            setPaymentProcessing(false);
            setTimeout(() => account.fetchAccount(true), 1000); // Give the account a brief moment to update after the transaction, as it is not instantaneous
            return ret;
          })
        )
        .catch(errors => {
          setLoading(false);
          setPaymentProcessing(false);
          logger(`${api.name} failed: `, errors);
          return {errors};
        });
    },
    [authAndContent.getValidToken, account.fetchAccount]
  );

  const existingPaymentMethodPurchase = paymentFlow(
    existingPaymentMethodPurchaseAPI
  );
  const existingPaymentMethodAddBalance = paymentFlow(
    existingPaymentMethodAddBalanceAPI
  );
  const oneTimePaymentPurchase = paymentFlow(oneTimePaymentPurchaseAPI);
  const oneTimePaymentAddBalance = paymentFlow(oneTimePaymentAddBalanceAPI);

  const createNewAutoPurchaseProfileInAccount = (
    res,
    createdAutoPurchaseProfile,
    __typename
  ) => {
    if (res?.errors) {
      return res;
    }
    account.setAccount(prev => {
      if (createdAutoPurchaseProfile) {
        createdAutoPurchaseProfile.__typename = __typename;
        return {
          ...prev,
          autoPurchaseProfiles: [
            ...prev.autoPurchaseProfiles,
            createdAutoPurchaseProfile
          ]
        };
      }
      return prev;
    });
    return res;
  };

  const disableTargetedAutoPurchaseProfileInAccount = (
    res,
    disabledAutoPurchaseProfile
  ) => {
    if (res?.errors) {
      return res;
    }
    account.setAccount(prev => {
      if (disabledAutoPurchaseProfile) {
        const newAutoPurchaseProfiles = prev.autoPurchaseProfiles.map(
          autoPurchaseProfile => {
            if (disabledAutoPurchaseProfile.id === autoPurchaseProfile.id) {
              return {...autoPurchaseProfile, ...disabledAutoPurchaseProfile};
            }
            return autoPurchaseProfile;
          }
        );
        return {...prev, autoPurchaseProfiles: newAutoPurchaseProfiles};
      }
      return prev;
    });
    return res;
  };

  const createAutoLoadProfile = (...args) =>
    handleApiCallWithToken(createAutoLoadProfileAPI, ...args).then(res =>
      handleApiRes({
        res,
        successMsg: 'Auto Load Created',
        reloadAccount: false,
        callback: res =>
          createNewAutoPurchaseProfileInAccount(
            res,
            res?.data?.account?.createAutoLoadProfile,
            'AutoLoadProfile'
          )
      })
    );

  const disableAutoLoadTransitAccount = (...args) =>
    handleApiCallWithToken(disableAutoLoadTransitAccountAPI, ...args).then(
      res =>
        handleApiRes({
          res,
          successMsg: 'Auto Load Disabled',
          reloadAccount: false,
          callback: res =>
            disableTargetedAutoPurchaseProfileInAccount(
              res,
              res?.data?.account?.disableAutoLoadTransitAccount
            )
        })
    );

  const createAutoRenewProfile = (...args) =>
    handleApiCallWithToken(createAutoRenewProfileAPI, ...args).then(res =>
      handleApiRes({
        res,
        successMsg: 'Auto Renew Created',
        reloadAccount: false,
        callback: res =>
          createNewAutoPurchaseProfileInAccount(
            res,
            res?.data?.account?.createAutoRenewProfile,
            'AutoRenewProfile'
          )
      })
    );

  const disableAutoRenewProfile = (...args) =>
    handleApiCallWithToken(disableAutoRenewProfileAPI, ...args).then(res =>
      handleApiRes({
        res,
        successMsg: 'Auto Renew Disabled',
        reloadAccount: false,
        callback: res =>
          disableTargetedAutoPurchaseProfileInAccount(
            res,
            res?.data?.account?.disableAutoRenew
          )
      })
    );

  return {
    selectedCardID,
    setSelectedCardID,
    paymentProcessing,
    setPaymentProcessing,
    oneTimePaymentToken,
    setOneTimePaymentToken,
    oneTimePaymentTimeLeft,
    addOneTimePaymentMethod,
    removeOneTimePaymentMethod,
    addPaymentMethod,
    removePaymentMethod,
    fetchPaymentToken,
    existingPaymentMethodPurchase,
    existingPaymentMethodAddBalance,
    oneTimePaymentPurchase,
    oneTimePaymentAddBalance,
    createAutoLoadProfile,
    disableAutoLoadTransitAccount,
    createAutoRenewProfile,
    disableAutoRenewProfile
  };
};

export default usePayment;
